import { Stack, Tooltip, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { t } from 'i18next'
import React, { useEffect, useRef, useState } from 'react'
import RecruitmentActionMenu from '../../components/Popup/RecruitmentActionMenu'
import DataTable from '../../components/Table/DataTable'
import defaultProps from '../../config/defaultProps'
import { useGetRecruitmentsQuery } from '../../store/redux/reducers/recruitmentSlice'
import { RequestProps } from '../../store/types'
import DecisionEnum from '../../store/types/enums/DecisionEnum'
import LocalStorageTypes from '../../store/types/enums/LocalStorageTypes'
import SessionStorageKeys from '../../store/types/enums/SessionStorageKeys'
import Recruitment from '../../store/types/models/Recruitment'
import usePersistedPage from '../../store/utils/usePersistedPage'
import { Column, FullName, Row } from '../../store/types/utils/DataTableUtils'
import { formatStringDate } from '../../store/utils/dateUtils'
import useOverflowCheck from '../../store/utils/useOverflowCheck'
import RecruitmentRow, { getDecisionStyle } from './RecruitmentRow'

type CandidateRecruitmentsTableProps = {
  candidateId: string | undefined
  handleRecruitmentRowClick: (recruitmentId: string) => void
}

const CandidateRecruitmentsTable = ({ candidateId, handleRecruitmentRowClick }: CandidateRecruitmentsTableProps) => {
  const [recruitmentProps, setRecruitmentProps] = useState<RequestProps>({
    ...defaultProps,
    'candidate.id': candidateId,
    sortField: 'completionDate',
    sortOrder: 1,
  })
  const { data: recruitmentResponse, isSuccess } = useGetRecruitmentsQuery(recruitmentProps)
  const [recruitments, setRecruitments] = useState<Recruitment[]>([])
  const [page, setPage] = usePersistedPage(SessionStorageKeys.RecruitmentsTablePage)
  const [recruitmentsCount, setRecruitmentsCount] = useState<number>(0)
  const commentsRef = useRef<HTMLDivElement | null>(null)
  const isTextWrapped = useOverflowCheck(commentsRef)

  useEffect(() => {
    if (isSuccess && recruitmentResponse) {
      setRecruitments(recruitmentResponse.content)
      setRecruitmentsCount(recruitmentResponse.totalElements)
    }
  }, [recruitmentResponse, isSuccess])

  const columns: Column[] = [
    { id: 'stage', label: t('page.candidateDetails.table.stage') },
    { id: 'step', label: t('page.candidateDetails.table.step') },
    { id: 'responsibles', label: t('page.candidateDetails.table.responsibles') },
    { id: 'comments', label: t('page.candidateDetails.table.comments') },
    { id: 'decision', label: t('page.candidateDetails.table.decision') },
    { id: 'completionDate', label: t('page.candidateDetails.table.completionDate') },
    { id: 'action', label: t('page.candidates.table.action') },
  ]

  const renderRecruitmentTileContent = (recruitment: Row, labelId: string, checkbox?: React.ReactNode) => {
    const renderResponsibles = (responsibles: FullName[]) => {
      if (!responsibles || responsibles.length === 0) {
        return '-'
      }
      return responsibles.map(responsible => `${responsible.firstName} ${responsible.lastName}`).join(', ')
    }

    return (
      <>
        <Stack direction="column" spacing={2} key={labelId}>
          {checkbox && checkbox}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box
              sx={{
                height: 'fit-content',
                border: '1px solid',
                borderRadius: 1,
                p: '5px',
                width: 'fit-content',
                borderColor: 'background.elevation8',
              }}
            >
              <Typography variant="body2" sx={getDecisionStyle(recruitment.decision)}>
                {Object.values(DecisionEnum).includes(recruitment.decision as DecisionEnum)
                  ? t(`decision.${recruitment.decision.toLowerCase()}`)
                  : '-'}
              </Typography>
            </Box>
            <RecruitmentActionMenu recruitmentId={recruitment.id} />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{recruitment.stage}</Typography>
            <Typography variant="body2" color="textSecondary">
              {formatStringDate(recruitment.completionDate, true)}
            </Typography>
          </Stack>
          {recruitment.responsibles.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="textSecondary" fontWeight={580}>
                {t('page.candidateDetails.table.responsibles')}:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {renderResponsibles(recruitment.responsibles)}
              </Typography>
            </Stack>
          )}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="textSecondary" fontWeight={580}>
              {t('page.candidateDetails.table.step')}:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {recruitment.step}
            </Typography>
          </Stack>
          {isTextWrapped && recruitment.comments ? (
            <Tooltip placement={'bottom-start'} title={recruitment.comments}>
              <Typography ref={commentsRef} noWrap>
                {recruitment.comments ? recruitment.comments : ''}
              </Typography>
            </Tooltip>
          ) : (
            <Typography ref={commentsRef} noWrap>
              {recruitment.comments ? recruitment.comments : ''}
            </Typography>
          )}
        </Stack>
      </>
    )
  }

  return (
    <DataTable
      renderTileContent={(recruitment: Row, labelId: string, checkbox?: React.ReactNode) =>
        renderRecruitmentTileContent(recruitment, labelId, checkbox)
      }
      renderTileCheckbox={false}
      unsortableColumns={[{ id: 'action', label: t('page.candidates.table.action') }]}
      columns={columns}
      data={recruitments}
      page={page}
      setPage={setPage}
      setRequestProps={setRecruitmentProps}
      renderRow={(recruitment: Row, labelId) => (
        <RecruitmentRow
          candidateId={candidateId}
          recruitment={recruitment}
          labelId={labelId}
          handleRecruitmentRowClick={handleRecruitmentRowClick}
        />
      )}
      singleSelection={false}
      totalElements={recruitmentsCount}
      translationForNotFound={'page.candidateDetails.table.notFound'}
      noEmptyRows
      commonRowsPerPage={
        localStorage.getItem(LocalStorageTypes.RecruitmentsTableRows)
          ? parseInt(localStorage.getItem(LocalStorageTypes.RecruitmentsTableRows)!)
          : undefined
      }
      localStorageRowsName={LocalStorageTypes.RecruitmentsTableRows}
    />
  )
}

export default CandidateRecruitmentsTable
